// TanStack Query hooks for task comments (constitution §5.4). A post or a delete refreshes the
// task's comments and its history.
import {
  createCommentsApi,
  createHttp,
  newIdempotencyKey,
  type Comment,
  type CommentPage,
} from '@cis/api-client';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

const comments = createCommentsApi(createHttp());

export const COMMENTS_PAGE_SIZE = 100;

export const commentsKey = (taskId: string) => ['tasks', taskId, 'comments'] as const;

export function useTaskComments(taskId: string, page: number): UseQueryResult<CommentPage> {
  return useQuery({
    queryKey: [...commentsKey(taskId), page],
    queryFn: ({ signal }) => comments.list(taskId, page, COMMENTS_PAGE_SIZE, signal),
    placeholderData: keepPreviousData,
  });
}

function useRefresh(taskId: string): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: commentsKey(taskId) }),
      queryClient.invalidateQueries({ queryKey: ['history', 'task', taskId] }),
    ]);
  };
}

export function useAddComment(taskId: string): UseMutationResult<Comment, Error, string> {
  const refresh = useRefresh(taskId);
  return useMutation({
    mutationFn: (body: string) => comments.add(taskId, body, newIdempotencyKey()),
    onSuccess: refresh,
  });
}

export function useDeleteComment(taskId: string): UseMutationResult<void, Error, number> {
  const refresh = useRefresh(taskId);
  return useMutation({
    mutationFn: (commentId: number) => comments.remove(taskId, commentId),
    onSettled: refresh,
  });
}
